import { memo, useEffect, useRef, useState } from 'react';
import { assetsOrgAssetsList, type OrgAssetResponse } from '@goldcast/api/content';
import VideoTabContent from './VideoTabContent';
import ImageTabContent from './ImageTabContent';
import PopoverCard from '@/components/molecules/PopoverCard/PopoverCard';
import appLogger from '@/services/logger';

type OverlayTab = 'video' | 'image';

interface ClipCustomizerOverlaysPopupProps {
  closePopup: () => void;
  initialTab?: OverlayTab;
}

const OVERLAY_TABS = [
  { id: 'video', label: 'Video', icon: 'IconMovie', trackingId: 'overlay-tab-video' },
  { id: 'image', label: 'Image', icon: 'IconPhoto', trackingId: 'overlay-tab-image' }
];

const ClipCustomizerOverlaysPopup = ({ closePopup, initialTab }: ClipCustomizerOverlaysPopupProps) => {
  const [activeTab, setActiveTab] = useState<OverlayTab>(initialTab ?? 'video');
  const prevInitialTabRef = useRef(initialTab);

  useEffect(() => {
    if (initialTab && initialTab !== prevInitialTabRef.current) {
      prevInitialTabRef.current = initialTab;
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [recentVideoAssets, setRecentVideoAssets] = useState<OrgAssetResponse[]>([]);
  const [recentImageAssets, setRecentImageAssets] = useState<OrgAssetResponse[]>([]);

  useEffect(() => {
    Promise.all([
      assetsOrgAssetsList({ queryParams: { type: 'VIDEO', limit: 6, ordering: '-created_at' } }),
      assetsOrgAssetsList({ queryParams: { type: 'IMAGE', limit: 6, ordering: '-created_at' } })
    ])
      .then(([videoRes, imageRes]) => {
        setRecentVideoAssets(videoRes.results);
        setRecentImageAssets(imageRes.results);
      })
      .catch(() => {
        appLogger.warn('[ClipCustomizerOverlaysPopup] recent_assets_fetch_failed');
      });
  }, []);

  return (
    <PopoverCard
      heading="Overlays"
      onClose={closePopup}
      closeTrackingId="overlay-popup-close"
      tabs={OVERLAY_TABS}
      activeTab={activeTab}
      onTabChange={id => setActiveTab(id as OverlayTab)}
    >
      <div className="flex flex-col gap-5">
        {activeTab === 'video' && <VideoTabContent closePopup={closePopup} recentAssets={recentVideoAssets} />}
        {activeTab === 'image' && <ImageTabContent closePopup={closePopup} recentAssets={recentImageAssets} />}
      </div>
    </PopoverCard>
  );
};

export default memo(ClipCustomizerOverlaysPopup);
